import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Pencil } from 'lucide-react';
import shortid from 'shortid';

import { FakeInput, UnstyledInput } from '@/shadcn/fakeInput';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';

import type { WidgetEditorBaseProps } from '@/components/ui/widget/views/text/text.view.type';
import { formatNumber, type NumberFormat, roundNumber } from '@/utils';
import { useFocus } from '@/utils/hooks';

const applyFormat = (value: unknown, format: NumberFormat) => {
  if (value === undefined || value === null || value === '') return '';
  return formatNumber(value, format);
};

type Props = Omit<WidgetEditorBaseProps<number | null>, 'value'> & {
  rawData: Maybe<string> | null;
  format?: NumberFormat;
  saveFormatted?: boolean;
  delay?: number;
};

export const NumberInputEditor = memo(
  ({
    rawData,
    onChange,
    placeholder = '',
    format = {},
    saveFormatted = false,
    delay = 250,
    header = '',
    disabled = false,
    identifier
  }: Props) => {
    // focus state
    const [isFocused, focus, blur, inputRef] = useFocus(false);

    const data =
      rawData === null || rawData === undefined ? '' : Number(rawData);

    // input state
    const [inputValue, setInputValue] = useState<number | string>();

    const updateInputNb = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
      },
      []
    );

    // on blur handler
    const handleBlur = useCallback(() => {
      if (saveFormatted && format.options?.maximumFractionDigits) {
        setInputValue(value => {
          if (value === '' || value === null || typeof value !== 'number') {
            return value;
          }
          return roundNumber(
            value,
            format.options?.maximumFractionDigits
          ).toString();
        });
      }
      blur();
    }, []);

    // change the number displayed if the data provided changes
    useEffect(
      () => {
        setInputValue(data);
      },
      [data] // Only re-call effect if data changes
    );

    // debounce update of the number input
    useEffect(
      () => {
        if (inputValue === data) return;

        // call onChange after delay
        const handler = setTimeout(() => {
          const newValue = inputValue === '' ? null : Number(inputValue);
          onChange(newValue);
        }, delay);

        // Cancel the timeout if value changes (also on unmount)
        // This is how we prevent debounced value from updating if value is changed ...
        // .. within the delay period. Timeout gets cleared and restarted.
        return () => {
          clearTimeout(handler);
        };
      },
      [inputValue] // Only re-call effect if inputStr changes
    );

    const id = useMemo(() => {
      if (header && header !== '') return header;
      return shortid.generate();
    }, []);

    const formatedValue = applyFormat(data, format);

    // just display the formatted number string if the field is disabled
    if (disabled) {
      return (
        <div
          className="cursor-not-allowed truncate px-2.5 py-1"
          data-cy={`number-editor-${identifier}-disabled`}
          title={formatedValue || undefined}
        >
          {formatedValue}
        </div>
      );
    }

    const displayedValue = isFocused ? inputValue : formatedValue;
    const isBatchEdit = !!header;

    return (
      <div className={cn('flex flex-col gap-2', !isBatchEdit && 'size-full')}>
        {isBatchEdit && (
          <Label data-cy={`number-editor-${identifier}-label`} htmlFor={id}>
            {header || '🚨 No provided header'}
          </Label>
        )}
        <FakeInput
          className={cn(
            'relative size-full',
            !isBatchEdit && [
              'rounded-none border-[1.5px] border-transparent bg-transparent',
              'focus-within:outline-none focus-visible:ring-0 dark:bg-transparent',
              isFocused
                ? 'border-primary'
                : 'hover:bg-accent dark:hover:bg-accent'
            ]
          )}
          onClick={focus}
        >
          <UnstyledInput
            className="w-full px-3 py-2"
            data-cy={`number-editor-${identifier}`}
            id={id}
            onBlur={handleBlur}
            onChange={updateInputNb}
            onFocus={focus}
            placeholder={placeholder}
            ref={inputRef}
            step="any"
            type={isFocused ? 'number' : 'text'}
            value={displayedValue}
          />
          {!isFocused && (
            <Pencil
              className="-translate-y-1/2 absolute top-1/2 right-2 text-muted-foreground/90"
              data-cy={`number-editor-${identifier}-icon`}
              size={18}
            />
          )}
        </FakeInput>
      </div>
    );
  }
);
